import { ChartId } from "@/features/analysis/types/chart";

export const TITLE_ICON_SIZE = 24;
export const WATERMARK_SIZE = 75;
export const WATERMARK_SIZE_DIALOG = 112.5;

export const REVENUE_BREAKDOWN_CHART_ID = "revenue-breakdown-chart";
export const PE_STREAM_CHART_ID = "pe-stream-chart";

const DEFAULT_COLOR_SET = [
  "#4F8EF7",
  "#34C759",
  "#FF9500",
  "#FF2D55",
  "#AF52DE",
  "#FFD60A",
];

// FIXME: 用不到，但需滿足 Chart 模組的 interface 處理
export const DUMMY_VALUE_FORMAT_TYPE = "large-number";
export const DUMMY_RANGE = "5y";

const colorSets = {
  "single-line-blue": ["#0FA4E9"],
  "single-line-green": ["#17A34A"],
  "single-line-yellow": ["#E9B30B"],
  "bar-and-line-blue": ["#0569A2", "#38BDF9"],
  "bar-and-line-green": ["#17803D", "#49DE80"],
  "bar-and-line-yellow": ["#A16207", "#FACC14"],
  "stacked-bar": [
    "#0084C6",
    "#38BDF9",
    "#E9B30B",
    "#FACC14",
    "#22C55D",
    "#49DE80",
  ],
  // Phase 2 使用
  "stacked-bar-2-15": [
    "#0369a1", // color/Sky/800
    "#0284c7", // color/Sky/600
    "#38bdf8", // color/Sky/400
    "#ca8a04", // color/Yellow/600
    "#facc15", // color/Yellow/400
    "#fde047", // color/Yellow/300
    "#16a34a", // color/Green/600
    "#22c55e", // color/Green/500
    "#4ade80", // color/Green/400
    "#f97316", // color/Orange/500
    "#fb923c", // color/Orange/400
    "#eab308", // color/Yellow/500
    "#ef4444", // color/Red/500
    "#f87171", // color/Red/400
    "#fca5a5", // color/Red/300
  ],
  "stacked-bar-2-6": [
    "#0284c7", // color/Sky/600
    "#38bdf8", // color/Sky/400
    "#EAB308", // color/Yellow/400
    "#FACC15", // color/Yellow/500
    "#22c55e", // color/Green/500
    "#4ade80", // color/Green/400
  ],
  "multi-line-1": ["#F97315", "#0569A2"],
  "multi-line-2": ["#38BDF9", "#F97315", "#17A34A"],
  "multi-line-3": [
    "#0669A1", // color/Sky/800
    "#EF4444", // color/Red/500
    "#FCA5A5", // color/Red/300
    "#CA8A04", // color/Yellow/600
    "#FDE047", // color/Yellow/300
    "#86EFAC", // color/Green/300
    "#6DD792", // color/Green/400
  ],
  "group-bar-green": ["#22C55D", "#F97315"],
  "group-bar-blue": ["#0084C6", "#F97315"],
} as const;

const iconSets = {
  "bar-blue": "/chart/bar-blue.svg",
  "bar-green": "/chart/bar-green.svg",
  "bar-yellow": "/chart/bar-yellow.svg",
  "bar-solid-blue": "/chart/bar-solid-blue.svg",
  "bar-solid-green": "/chart/bar-solid-green.svg",
  "bar-solid-yellow": "/chart/bar-solid-yellow.svg",
  "line-blue": "/chart/line-blue.svg",
  "line-green": "/chart/line-green.svg",
  "line-yellow": "/chart/line-yellow.svg",
  "line-solid-blue": "/chart/line-solid-blue.svg",
  "line-solid-green": "/chart/line-solid-green.svg",
  "line-solid-yellow": "/chart/line-solid-yellow.svg",
} as const;

const chartColorSetMap: Record<
  ChartId,
  {
    chartColorSet: keyof typeof colorSets | (keyof typeof colorSets)[];
    chartIcon: (typeof iconSets)[keyof typeof iconSets];
  }
> = {
  // financial
  "revenue-chart": {
    chartColorSet: "bar-and-line-green",
    chartIcon: iconSets["line-green"],
  },
  "eps-chart": {
    chartColorSet: "bar-and-line-blue",
    chartIcon: iconSets["line-blue"],
  },
  "eps-diluted-chart": {
    chartColorSet: "bar-and-line-yellow",
    chartIcon: iconSets["line-yellow"],
  },
  "revenue-breakdown-chart": {
    // 動態顏色選擇：索引 0-6 用 stacked-bar-2-6，索引 7+ 用 stacked-bar-2-15
    // 意思是：dataLength <= 7 用 6 色，dataLength > 7 用 15 色
    chartColorSet: [
      "stacked-bar-2-6",
      "stacked-bar-2-6",
      "stacked-bar-2-6",
      "stacked-bar-2-6",
      "stacked-bar-2-6",
      "stacked-bar-2-6",
      "stacked-bar-2-6",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
      "stacked-bar-2-15",
    ],
    chartIcon: iconSets["bar-blue"],
  },
  "net-income-chart": {
    chartColorSet: "bar-and-line-blue",
    chartIcon: iconSets["line-blue"],
  },
  "operating-expenses-chart": {
    chartColorSet: "stacked-bar",
    chartIcon: iconSets["bar-blue"],
  },
  "cash-flow-chart": {
    chartColorSet: "group-bar-green",
    chartIcon: iconSets["bar-solid-blue"],
  },
  "operation-income-chart": {
    chartColorSet: "bar-and-line-yellow",
    chartIcon: iconSets["line-yellow"],
  },
  "cash-and-debt-chart": {
    chartColorSet: "group-bar-blue",
    chartIcon: iconSets["bar-solid-blue"],
  },

  // valuation
  "pe-ratio-chart": {
    chartColorSet: "single-line-blue",
    chartIcon: iconSets["line-solid-blue"],
  },
  "ps-ratio-chart": {
    chartColorSet: "single-line-green",
    chartIcon: iconSets["line-solid-green"],
  },
  "pb-ratio-chart": {
    chartColorSet: "single-line-yellow",
    chartIcon: iconSets["line-solid-yellow"],
  },
  "forward-pe-chart": {
    chartColorSet: "single-line-green",
    chartIcon: iconSets["line-solid-green"],
  },
  "ev-to-ebitda-chart": {
    chartColorSet: "single-line-yellow",
    chartIcon: iconSets["line-solid-yellow"],
  },
  "ev-to-sales-chart": {
    chartColorSet: "single-line-blue",
    chartIcon: iconSets["line-solid-blue"],
  },
  "pe-stream-chart": {
    chartColorSet: "multi-line-3",
    chartIcon: iconSets["line-solid-blue"],
  },

  // growth
  "peg-ratio-chart": {
    chartColorSet: "single-line-green",
    chartIcon: iconSets["line-green"],
  },
  "eps-surprise-chart": {
    chartColorSet: "multi-line-1",
    chartIcon: iconSets["line-blue"],
  },
  "revenue-surprise-chart": {
    chartColorSet: "multi-line-1",
    chartIcon: iconSets["line-blue"],
  },

  // profitability
  "ros-chart": {
    chartColorSet: "single-line-blue",
    chartIcon: iconSets["line-blue"],
  },
  "operating-roa-chart": {
    chartColorSet: "multi-line-2",
    chartIcon: iconSets["line-solid-blue"],
  },
  "profit-margin-chart": {
    chartColorSet: "multi-line-2",
    chartIcon: iconSets["line-solid-blue"],
  },
};

export const getChartColorSet = (chartId: ChartId, dataLength: number) => {
  const { chartColorSet } = chartColorSetMap[chartId];

  let selectedColorSet: string;
  if (Array.isArray(chartColorSet)) {
    selectedColorSet =
      chartColorSet[Math.min(dataLength - 1, chartColorSet.length - 1)];
  } else {
    selectedColorSet = chartColorSet;
  }

  const colorSet =
    colorSets[selectedColorSet as keyof typeof colorSets] || DEFAULT_COLOR_SET;

  return [...colorSet];
};

export const getChartIconSrc = (chartId: ChartId) => {
  const { chartIcon } = chartColorSetMap[chartId];
  return chartIcon;
};
